import * as React from 'react';
import type { JSX } from 'react';
import styled from 'styled-components';

import type { SpacingValue } from '../../theme';
import type { AlignItems, JustifyContent, JustifyItems } from '../flexbox';

export enum StackDirectionType {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}
interface Props {
  direction?: 'vertical' | 'horizontal';
  alignItems?: AlignItems;
  justifyContent?: JustifyContent;
  gap: SpacingValue;
  children: React.ReactNode;
  width?: string;
  height?: string;
  testId?: string;
  isFluid?: boolean;
  justifyItems?: JustifyItems;
}

interface StyledProps {
  gap: SpacingValue;
  columnCount: number;
  width?: string;
  height?: string;
  isFluid: boolean;
  direction: 'vertical' | 'horizontal';
  alignItems: AlignItems;
  justifyContent: JustifyContent;
  justifyItems?: JustifyItems;
}

/**
 * @deprecated Use tailwind css props for flex instead https://tailwindcss.com/docs/flex
 */
export function Stack(props: Props): JSX.Element | null {
  const {
    children,
    gap,
    testId,
    alignItems = 'initial',
    justifyContent = 'initial',
    width = 'auto',
    height = 'auto',
    isFluid = false,
    direction = 'horizontal',
    justifyItems,
  } = props;

  if (!children) return null;

  const columnCount = React.Children.map(children, (item) => item)?.filter((el) => !!el).length;

  return (
    <StyledStack
      direction={direction}
      gap={gap}
      isFluid={isFluid}
      columnCount={columnCount ?? 0}
      alignItems={alignItems}
      justifyContent={justifyContent}
      width={width}
      height={height}
      data-testid={testId}
      justifyItems={justifyItems}
    >
      {children}
    </StyledStack>
  );
}

const StyledStack = styled.div<StyledProps>`
  display: grid;
  ${(props: StyledProps) => {
    const { direction, columnCount, isFluid, gap, alignItems, justifyContent, justifyItems, height, width } = props;
    const childSize = isFluid ? '1fr' : 'max-content';

    if (direction === 'horizontal') {
      return {
        alignItems,
        height,
        justifyContent,
        justifyItems,
        gridAutoFlow: 'column',
        gridColumnGap: `${gap}px`,
        gridTemplateColumns: `repeat(${columnCount}, ${childSize})`,
        width,
      };
    }

    return {
      gridTemplateRows: `repeat(${columnCount}, ${childSize})`,
      gridAutoFlow: 'row',
      gridRowGap: `${gap}px`,
      alignItems,
      justifyContent,
      justifyItems,
      height,
      width,
    };
  }}
`;
